import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Message,
  Modal,
  Select,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';

const auctionLocationInit = {
  locationId: '',
  contactId: '',
  contactName: '',
  title: '',
  contactEmail: '',
  phone: '',
  contactType: '',
  guaranteeConfirm: false,
  guaranteeCancel: false,
  returns: false,
  vehNotCovered: false,
};

export const AuctionLocationContactAction = {
  Create: 'Create',
  Update: 'Update',
  Delete: 'Delete',
};

const ContactType = ['Arbitration', 'Titles', 'Finance', 'Meridian', 'Other'];

function AuctionContactForm(props) {
  const {
    isModalOpen,
    title,
    onCloseHandler,
    isLoading,
    onSubmitHandler,
    locationId,
    errorMessage,
    successMessage,
    onClearError,
    contactData,
  } = props;

  const [errors, setErrors] = useState({});
  const [contactTypeOptions, setContactTypeOptions] = useState([]);
  const [formData, setFormData] = useState(auctionLocationInit);

  // Remove duplicate loading state - use isLoading prop directly
  const onClose = useCallback(() => {
    onCloseHandler();
    // Reset to completely clean state
    setFormData({
      ...auctionLocationInit,
      locationId: Number(locationId),
    });
    setErrors({});
    // Parent component handles success message clearing
  }, [onCloseHandler, locationId]);

  // Fix form reset to preserve locationId
  useEffect(() => {
    if (successMessage) {
      setFormData(() => ({
        ...auctionLocationInit,
        locationId: Number(locationId),
      }));
      setErrors({});
    }
  }, [successMessage, locationId]);

  useEffect(() => {
    const data = contactData || auctionLocationInit;
    setFormData({
      ...data,
      locationId: Number(locationId),
    });
  }, [contactData]);

  // Make sure formData.locationId is always the incoming prop
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      locationId: Number(locationId),
    }));
  }, [locationId]);

  useEffect(() => {
    setContactTypeOptions(
      ContactType.map(type => ({
        key: type,
        text: type,
        value: type,
      }))
    );
  }, []);

  const validate = useCallback(() => {
    const errs = {};
    if (!formData.contactName.trim()) {
      errs.contactName = ['Name is required.'];
    } else {
      const nameErrors = [];
      if (formData.contactName.length > 100) {
        nameErrors.push('Name must not exceed 100 characters.');
      }
      if (nameErrors.length) errs.contactName = nameErrors;
    }

    if (!formData.title.trim()) {
      errs.title = ['Title is required.'];
    } else {
      const titleErrors = [];
      if (formData.title.length > 100) {
        titleErrors.push('Title must not exceed 100 characters.');
      }
      if (titleErrors.length) errs.title = titleErrors;
    }

    const rawEmail = formData.contactEmail;
    const emailTrimmed = rawEmail.trim();
    if (!emailTrimmed) {
      errs.contactEmail = ['Email is required.'];
    } else {
      const emailErrors = [];

      if (/[;,]/.test(emailTrimmed)) {
        emailErrors.push('Only one email address is allowed.');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        emailErrors.push('Please enter a valid email address.');
      }

      if (emailTrimmed.length > 250) {
        emailErrors.push('Email must not exceed 250 characters.');
      }

      if (emailErrors.length) errs.contactEmail = emailErrors;
    }

    if (!formData.phone.trim()) {
      errs.phone = ['Phone number is required.'];
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errs.phone = [
        'Phone number must contain only digits and be 10 characters long.',
      ];
    }

    if (formData.contactType.length === 0) {
      errs.contactType = ['Please select at least one contact type.'];
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleChange = (_, {
    name, value, checked, type,
  }) => {
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear only if error exists for this field
    if (errors[name] && errors[name].length > 0) {
      setErrors(prev => ({ ...prev, [name]: [] }));
    }
  };

  const handleContactTypeChange = (_, { name, value }) => {
    setFormData(prev => ({
      ...prev,
      [name]: value.join(','),
    }));
    if (errors[name] && errors[name].length > 0) {
      setErrors(prev => ({ ...prev, [name]: [] }));
    }
  };

  // Fix submitForm dependencies and use isLoading prop
  const submitForm = useCallback(
    async e => {
      e.preventDefault();
      if (isLoading) return;

      if (validate()) {
        onSubmitHandler({
          ...formData,
          action:
            contactData && contactData.contactId
              ? AuctionLocationContactAction.Update
              : AuctionLocationContactAction.Create,
        });
      }
    },
    [formData, isLoading, onSubmitHandler, validate, contactData]
  );

  return (
    <Modal
      size='large'
      closeIcon
      onClose={onClose}
      open={isModalOpen}
      closeOnEscape
      closeOnRootNodeClick={false}
    >
      <Modal.Header style={{ borderBottom: 'none' }}>{title}</Modal.Header>
      <Modal.Content>
        {errorMessage && !successMessage && (
          <Message negative content={errorMessage} onDismiss={onClearError} />
        )}
        <Form>
          <Modal.Description>
            <Form.Group>
              <Form.Field
                width={5}
                required
                error={!isEmpty(errors.contactName)}
              >
                <label htmlFor='contactName'>Name</label>
                <Input
                  id='contactName'
                  name='contactName'
                  autoComplete='off'
                  value={formData.contactName}
                  onChange={handleChange}
                />
                {errors.contactName && (
                  <span className='error-msg'>
                    {errors.contactName.join(' ')}
                  </span>
                )}
              </Form.Field>
              <Form.Field width={5} required error={!isEmpty(errors.title)}>
                <label htmlFor='title'>Title</label>
                <Input
                  id='title'
                  name='title'
                  autoComplete='off'
                  value={formData.title}
                  onChange={handleChange}
                />
                {errors.title && (
                  <span className='error-msg'>{errors.title.join(' ')}</span>
                )}
              </Form.Field>
              <Form.Field width={5} required error={!isEmpty(errors.phone)}>
                <label htmlFor='phone'>Phone Number</label>
                <Input
                  id='phone'
                  name='phone'
                  autoComplete='off'
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <span className='error-msg'>{errors.phone.join(' ')}</span>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field
                width={5}
                required
                error={!isEmpty(errors.contactEmail)}
              >
                <label htmlFor='contactEmail'>Email</label>
                <Input
                  id='contactEmail'
                  name='contactEmail'
                  autoComplete='off'
                  value={formData.contactEmail}
                  onChange={handleChange}
                />
                {errors.contactEmail && (
                  <span className='error-msg'>
                    {errors.contactEmail.join(' ')}
                  </span>
                )}
              </Form.Field>

              <Form.Field
                width={5}
                required
                error={!isEmpty(errors.contactType)}
              >
                <label htmlFor='contactType'>Contact Type</label>
                <Select
                  id='contactType'
                  search
                  multiple
                  name='contactType'
                  placeholder='Select Contact Type'
                  options={contactTypeOptions}
                  closeOnBlur
                  selectOnBlur={false}
                  value={formData.contactType ? formData.contactType.split(',') : []}
                  onChange={handleContactTypeChange}
                />
                {errors.contactType && (
                  <span className='error-msg'>
                    {errors.contactType.join(' ')}
                  </span>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <Checkbox
                label='Guarantee Confirm'
                name='guaranteeConfirm'
                checked={formData.guaranteeConfirm}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='Guarantee Cancel'
                name='guaranteeCancel'
                checked={formData.guaranteeCancel}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='Returns'
                name='returns'
                checked={formData.returns}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='Vehicle Not Covered'
                name='vehNotCovered'
                checked={formData.vehNotCovered}
                onChange={handleChange}
              />
            </Form.Field>
          </Modal.Description>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color='green' loading={isLoading} onClick={submitForm}>
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

AuctionContactForm.propTypes = {
  isModalOpen: PropTypes.bool,
  title: PropTypes.string,
  onCloseHandler: PropTypes.func,
  isLoading: PropTypes.bool,
  onSubmitHandler: PropTypes.func,
  locationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  errorMessage: PropTypes.string,
  successMessage: PropTypes.string,
  onClearError: PropTypes.func,
  contactData: PropTypes.shape({
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contactName: PropTypes.string,
    title: PropTypes.string,
    contactEmail: PropTypes.string,
    phone: PropTypes.string,
    contactType: PropTypes.string,
    guaranteeConfirm: PropTypes.bool,
    guaranteeCancel: PropTypes.bool,
    returns: PropTypes.bool,
    vehNotCovered: PropTypes.bool,
  }),
};

AuctionContactForm.defaultProps = {
  isModalOpen: false,
  title: '',
  onCloseHandler: () => {},
  isLoading: false,
  onSubmitHandler: () => {},
  locationId: '',
  errorMessage: '',
  successMessage: '',
  onClearError: () => {},
  contactData: null,
};

export default AuctionContactForm;
